import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { Search, User, Phone, Hash, ChevronRight, AlertCircle } from "lucide-react";
import { usePatient } from "../context/PatientContext";
import { useAuth } from "../context/AuthProvider";
import { useTheme } from "../context/ThemeContext";
import axios from "axios";
import CONFIG from "../config";
import TransliteratedInput from "./common/TransliteratedInput";
import { Calendar as CalendarIcon, Clock, RefreshCcw, Filter } from "lucide-react";
import AppointmentCalendar from "./Calendar";
import { FaWhatsapp } from "react-icons/fa";

const SelectPatientAndCases = () => {
    const { verifyTokenAndRedirect } = useAuth();
    const { setPatientData, doctorInfo } = usePatient();
    const { theme, isDarkMode } = useTheme();

    const [isSearching, setIsSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [todayAppointments, setTodayAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctorFilter, setSelectedDoctorFilter] = useState("all");
    const [showDoctorFilter, setShowDoctorFilter] = useState(false);

    const navigate = useNavigate();
    const baseURL = CONFIG.BASE_URL;
    const token = localStorage.getItem("authToken");

    useEffect(() => {
        verifyTokenAndRedirect();
    }, [verifyTokenAndRedirect]);

    const fetchClinicDoctors = useCallback(async () => {
        if (!doctorInfo?.CLINIC_ID) return;
        try {
            const response = await axios.get(`${baseURL}/doctors/clinic/${doctorInfo.CLINIC_ID}/doctors`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    doctorId: doctorInfo.MANDALI_ID,
                    clinicId: doctorInfo.CLINIC_ID,
                    roleId: doctorInfo.MANDALI_ROLE,
                },
            });
            if (response.data.success) {
                setDoctors(response.data.doctors || []);
            }
        } catch (error) {
            console.error("Error fetching clinic doctors:", error);
        }
    }, [baseURL, doctorInfo, token]);

    useEffect(() => {
        if (doctorInfo?.CLINIC_ID) {
            fetchClinicDoctors();
        }
    }, [doctorInfo?.CLINIC_ID, fetchClinicDoctors]);

    const fetchTodayAppointments = useCallback(async () => {
        if (!doctorInfo?.MANDALI_ID) return;
        setLoadingAppointments(true);
        try {
            const now = new Date();
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            const response = await axios.get(`${baseURL}/doctors/calendar/appointments`, {
                params: { view: 'month', date: today },
                headers: {
                    Authorization: `Bearer ${token}`,
                    doctorId: doctorInfo.MANDALI_ID,
                    clinicId: doctorInfo.CLINIC_ID,
                    roleId: doctorInfo.MANDALI_ROLE,
                },
            });
            if (response.data.success) {
                setTodayAppointments(response.data.appointments || []);
            }
        } catch (error) {
            console.error("Error fetching today's appointments:", error);
        } finally {
            setLoadingAppointments(false);
        }
    }, [baseURL, doctorInfo, token]);

    const getFilteredAppointments = useCallback(() => {
        if (selectedDoctorFilter === "all") return todayAppointments;
        return todayAppointments.filter(apt => String(apt.DOCTOR_ID) === String(selectedDoctorFilter));
    }, [todayAppointments, selectedDoctorFilter]);

    useEffect(() => {
        fetchTodayAppointments();
    }, [fetchTodayAppointments]);

    const handleSearch = useCallback(async () => {
        if (!searchTerm.trim()) return;

        // Fallback: if context isn't updated, try to decode token for roleId
        let currentRoleId = doctorInfo.MANDALI_ROLE;
        let currentDoctorId = doctorInfo.MANDALI_ID;
        let currentClinicId = doctorInfo.CLINIC_ID;

        if (!currentRoleId && token) {
            try {
                const decoded = jwtDecode(token);
                currentRoleId = decoded.userData.MANDALI_ROLE;
                currentDoctorId = decoded.userData.MANDALI_ID;
                currentClinicId = decoded.userData.CLINIC_ID;
            } catch (e) {
                console.error("Error decoding token in search", e);
            }
        }

        console.log("🔍 Searching with headers:", {
            roleId: currentRoleId,
            doctorId: currentDoctorId,
            clinicId: currentClinicId
        });

        if (!currentRoleId) {
            console.error("❌ Still missing roleId after fallback check");
        }

        setLoading(true);
        try {
            const response = await axios.get(`${baseURL}/patients/search-patients/${encodeURIComponent(searchTerm)}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    doctorId: currentDoctorId,
                    clinicId: currentClinicId,
                    roleId: currentRoleId
                }
            });
            setSearchResults(response.data);
            setIsSearching(true);
        } catch (error) {
            console.error("Search API Error:", error.response?.data || error.message);
            setSearchResults([]);
            setIsSearching(true);
        } finally {
            setLoading(false);
        }
    }, [baseURL, doctorInfo, searchTerm, token]);

    const handlePatientSelect = (patient) => {
        setPatientData(patient);
        navigate(`/patient/${patient.PATIENT_ID}`);
    };

    const tableHeaderStyle = {
        backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        color: theme.text,
        borderColor: theme.border,
        fontWeight: "700",
        textTransform: "uppercase",
        fontSize: "11px",
        letterSpacing: "0.15em",
        padding: "1.25rem 2rem",
        borderBottom: `2px solid ${theme.border}`,
        textAlign: "left"
    };

    const tableCellStyle = {
        color: theme.text,
        borderColor: theme.border,
        fontSize: "15px",
        padding: "1.25rem 2rem",
        borderBottom: `1px solid ${theme.border}`
    };

    return (
        <div className="min-h-screen p-4 md:px-0 md:py-8 transition-colors duration-500">
            <div className="max-w-[1700px] mx-auto flex flex-col lg:flex-row gap-6 items-start px-4">

                {/* Left Section: Patient Search & Directory */}
                <div className="flex-1 w-full order-2 lg:order-1">
                    {/* Search Header Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="w-full"
                    >
                        <h2
                            className="text-3xl md:text-4xl font-serif mb-6"
                            style={{ color: theme.text }}
                        >
                            Patient Directory
                        </h2>

                        {/* Search Input Group */}
                        <div
                            className="relative flex items-center p-2 rounded-2xl shadow-2xl transition-all"
                            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}
                        >
                            <div className="pl-4 text-gray-400">
                                <Search size={22} style={{ color: theme.primary }} />
                            </div>
                            <div className="flex-1">
                                <TransliteratedInput
                                    type="text"
                                    placeholder="Search by patient name or phone..."
                                    className="w-full p-4 bg-transparent outline-none font-medium text-lg"
                                    style={{ color: theme.text }}
                                    value={searchTerm}
                                    onChangeText={(text) => setSearchTerm(text)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                            <button
                                onClick={handleSearch}
                                disabled={loading}
                                className="px-6 py-3 rounded-xl font-bold text-white transition-all active:scale-95 disabled:opacity-50"
                                style={{ backgroundColor: theme.primary }}
                            >
                                {loading ? "..." : "Search"}
                            </button>
                        </div>
                    </motion.div>

                    {/* Results Section */}
                    <div className="w-full mt-12">
                        <AnimatePresence mode="wait">
                            {isSearching ? (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="space-y-4"
                                >
                                    <div className="flex justify-between items-center px-4 mb-4">
                                        <h3 className="text-lg font-bold uppercase tracking-widest opacity-80" style={{ color: "rgba(255,255,255,0.6)" }}>
                                            Results Found: {searchResults.length}
                                        </h3>
                                    </div>

                                    {searchResults.length > 0 ? (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.98 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border"
                                            style={{ backgroundColor: theme.card, borderColor: theme.border }}
                                        >
                                            <div className="overflow-x-auto">
                                                <table className="w-full border-collapse" style={{ backgroundColor: theme.inputBg }}>
                                                    <thead>
                                                        <tr style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}>
                                                            <th style={tableHeaderStyle}>Patient Information</th>
                                                            <th style={tableHeaderStyle}>Patient ID</th>
                                                            <th style={tableHeaderStyle}>Phone Number</th>
                                                            <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y" style={{ borderColor: theme.border }}>
                                                        {searchResults.map((patient, index) => (
                                                            <motion.tr
                                                                key={patient.PATIENT_ID}
                                                                initial={{ opacity: 0, x: -10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                transition={{ delay: index * 0.05 }}
                                                                className="group cursor-pointer transition-colors"
                                                                onClick={() => handlePatientSelect(patient)}
                                                                style={{
                                                                    borderBottom: `1px solid ${theme.border}`,
                                                                    backgroundColor: isDarkMode ? theme.inputBg : 'inherit'
                                                                }}
                                                                whileHover={{
                                                                    backgroundColor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)"
                                                                }}
                                                            >
                                                                <td style={tableCellStyle}>
                                                                    <div className="flex items-center gap-4">
                                                                        <div className="w-11 h-11 rounded-2xl flex items-center justify-center font-bold text-lg border shadow-sm"
                                                                            style={{ backgroundColor: theme.primary + '15', color: theme.primary, borderColor: theme.primary + '20' }}>
                                                                            <User size={20} />
                                                                        </div>
                                                                        <div>
                                                                            <p className="font-bold text-lg" style={{ color: theme.text }}>{patient.PATIENT_NAME}</p>
                                                                            <p className="text-xs font-semibold opacity-60 uppercase tracking-wider" style={{ color: theme.textSecondary }}>Patient File</p>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td style={tableCellStyle}>
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primary + '10' }}>
                                                                            <Hash size={16} style={{ color: theme.primary }} className="opacity-70" />
                                                                        </div>
                                                                        <span className="font-semibold text-base" style={{ color: theme.text }}>
                                                                            {patient.PATIENT_ID}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td style={tableCellStyle}>
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="p-2 rounded-lg" style={{ backgroundColor: theme.primary + '10' }}>
                                                                            <Phone size={16} style={{ color: theme.primary }} className="opacity-70" />
                                                                        </div>
                                                                        <div
                                                                            className="p-2 rounded-lg cursor-pointer transition-all hover:scale-110 active:scale-95"
                                                                            style={{ backgroundColor: '#25D36620', color: '#25D366' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                const cleanNumber = patient.PATIENT_PHONE.replace(/\D/g, '');
                                                                                window.open(`https://wa.me/${cleanNumber}`, '_blank');
                                                                            }}
                                                                            title="Open in WhatsApp"
                                                                        >
                                                                            <FaWhatsapp size={16} />
                                                                        </div>
                                                                        <span className="font-semibold text-base" style={{ color: theme.text }}>
                                                                            {patient.PATIENT_PHONE}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td style={{ ...tableCellStyle, textAlign: "right" }}>
                                                                    <button
                                                                        className="px-6 py-2.5 rounded-full text-sm font-bold border transition-all duration-200 flex items-center justify-center gap-2 float-right shadow-sm group-hover:bg-opacity-80"
                                                                        style={{
                                                                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                                                            borderColor: theme.border,
                                                                            color: theme.primary
                                                                        }}
                                                                    >
                                                                        Open File
                                                                        <ChevronRight size={14} />
                                                                    </button>
                                                                </td>
                                                            </motion.tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            className="flex flex-col items-center justify-center p-20 rounded-[2.5rem] border border-dashed"
                                            style={{ backgroundColor: theme.card, borderColor: theme.border }}
                                        >
                                            <AlertCircle size={64} className="mb-6 opacity-20" style={{ color: theme.text }} />
                                            <p className="text-2xl font-bold opacity-30" style={{ color: theme.text }}>No patients found for "{searchTerm}"</p>
                                            <button
                                                onClick={() => navigate('/addpatient')}
                                                className="mt-6 px-8 py-3 rounded-2xl font-bold text-white transition-all hover:scale-105"
                                                style={{ backgroundColor: theme.primary }}
                                            >
                                                Register New Patient
                                            </button>
                                        </motion.div>
                                    )}
                                </motion.div>
                            ) : (
                                /* Initial State / Prompt */
                                <div className="text-center opacity-60 py-20">
                                    <p className="text-lg italic" style={{ color: "rgba(255,255,255,0.6)" }}>
                                        Enter a name or phone number to begin searching...
                                    </p>
                                </div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Right Section: Today's Schedule Card */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="w-full lg:w-[400px] shrink-0 order-1 lg:order-2"
                >
                    <div
                        className="rounded-[2.5rem] overflow-hidden shadow-2xl border flex flex-col h-[700px]"
                        style={{ backgroundColor: theme.card, borderColor: theme.border }}
                    >
                        {/* Card Header */}
                        <div className="p-6 border-b" style={{ borderColor: theme.border }}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.primary + '15', color: theme.primary }}>
                                        <CalendarIcon size={20} />
                                    </div>
                                    <h3 className="text-xl font-bold" style={{ color: theme.text }}>Today's Schedule</h3>
                                </div>
                                <button
                                    onClick={fetchTodayAppointments}
                                    disabled={loadingAppointments}
                                    className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                    style={{ color: theme.primary }}
                                >
                                    <RefreshCcw size={18} className={loadingAppointments ? "animate-spin" : ""} />
                                </button>
                            </div>

                            {/* Doctor Filter Dropdown in Card Header */}
                            {doctors.length > 0 && (
                                <div className="relative">
                                    <button
                                        onClick={() => setShowDoctorFilter(!showDoctorFilter)}
                                        className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-bold border transition-all"
                                        style={{
                                            backgroundColor: theme.inputBg,
                                            borderColor: theme.border,
                                            color: theme.text
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Filter size={14} className="opacity-60" />
                                            <span className="truncate">
                                                {selectedDoctorFilter === "all"
                                                    ? "All Doctors"
                                                    : doctors.find(d => String(d.MANDALI_ID) === selectedDoctorFilter)?.MANDALI_FULL_NAME || "Select Doctor"}
                                            </span>
                                        </div>
                                        <ChevronRight size={14} className={`transform transition-transform ${showDoctorFilter ? 'rotate-90' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {showDoctorFilter && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                className="absolute top-full left-0 right-0 mt-2 rounded-2xl shadow-2xl border overflow-hidden z-20"
                                                style={{ backgroundColor: theme.card, borderColor: theme.border }}
                                            >
                                                <div
                                                    className={`px-4 py-3 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-sm font-bold ${selectedDoctorFilter === "all" ? "text-primary" : ""}`}
                                                    style={{ color: selectedDoctorFilter === "all" ? theme.primary : theme.text }}
                                                    onClick={() => {
                                                        setSelectedDoctorFilter("all");
                                                        setShowDoctorFilter(false);
                                                    }}
                                                >
                                                    All Doctors
                                                </div>
                                                {doctors.map(dr => (
                                                    <div
                                                        key={dr.MANDALI_ID}
                                                        className={`px-4 py-3 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-sm font-bold ${selectedDoctorFilter === String(dr.MANDALI_ID) ? "text-primary" : ""}`}
                                                        style={{ color: selectedDoctorFilter === String(dr.MANDALI_ID) ? theme.primary : theme.text }}
                                                        onClick={() => {
                                                            setSelectedDoctorFilter(String(dr.MANDALI_ID));
                                                            setShowDoctorFilter(false);
                                                        }}
                                                    >
                                                        {dr.MANDALI_FULL_NAME}
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}
                        </div>

                        {/* Schedule Content */}
                        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                            <AppointmentCalendar
                                mode="view"
                                defaultView="day"
                                hideHeader={true}
                                existingAppointments={todayAppointments}
                                doctorInfo={doctorInfo}
                                loading={loadingAppointments}
                                fetchAppointment={fetchTodayAppointments}
                                userRole={doctorInfo?.MANDALI_ROLE || 3}
                                clinicId={doctorInfo?.CLINIC_ID}
                                theme={theme}
                                isDarkMode={isDarkMode}
                            />
                        </div>

                        {/* Card Footer */}
                        <div className="p-6 border-t bg-black/5 dark:bg-white/5 text-center" style={{ borderColor: theme.border }}>
                            <button
                                onClick={() => navigate('/calendar')}
                                className="text-sm font-bold underline transition-colors"
                                style={{ color: theme.primary }}
                            >
                                View Full Calendar
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default SelectPatientAndCases;
